/**
 * ChangeRadar — book-wide "what changed and needs attention" section (TASK-477).
 *
 * Lead block of the dashboard's "Attention Today" section (NOT a separate page/tab):
 * the cross-client signal layer above the per-item reminder cards. Pure consumer —
 * every impact/exposure figure comes from the server payload. Live-refreshes when the
 * server rebuilds the radar and pushes `radar_updated` over the alert WebSocket, which
 * invalidates the ['radar'] React Query (see useAlertWebSocket).
 */

import React, { useCallback, useEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Radar, AlertTriangle, ChevronDown, ChevronRight, ListPlus, PanelLeftClose, Search, Users } from 'lucide-react';
import { useRadar } from '../../hooks/useRadar';
import { RadarEvent, ImpactedClient } from '../../services/radarService';
import { useCanvasStore } from '../../stores/canvasStore';
import { ALERT_PAGE_SIZE, useAlertStore } from '../../stores/alertStore';
import { useMakeTaskFromSignal, type SignalTaskInput } from '../../hooks/useMakeTaskFromSignal';
import AlertRow from './alerts/AlertRow';
import AlertGroupCard from './alerts/AlertGroupCard';
import { SignalUrgency } from './SignalUrgency';
import { groupAlerts } from '../../utils/groupAlerts';
import { clientDisplayName, namesAClient } from '../../utils/clientDisplayName';
import { primaryTriageCount, unresolvedAlerts } from '../../utils/triageCounts';
import type { DashboardAlert } from '../../types/dashboard';
import { policyPermits } from '../../utils/signalPolicy';
import { useViewerRole } from '../../contexts/AccountContext';
import { LENSES, LENS_LABEL, signalsRailRenders, type DeskLens, type Lens } from '../../hooks/useDeskLens';

// TASK-725/728: `internal` was "everything except news" — i.e. `all` minus the contents of
// the `news` tab, which already exists as its own filter. With `news_items` holding a single
// row on the live book (the "0 rows" in the original note was a contextless read of a
// FORCE-RLS'd table), All and Internal rendered near-identical lists, and even with news
// present the distinction only restates what the News tab already offers. Two tabs showing one list
// is a surface that implies a difference it cannot deliver, so the duplicate is gone rather
// than relabelled.
type Tab = 'all' | 'news' | 'alerts';

const TABS: readonly Tab[] = ['all', 'news', 'alerts'] as const;
const DEFAULT_TAB: Tab = 'all';

/**
 * TASK-726: the Radar's presentation state lives in the URL, not in `useState`.
 *
 * This uses the convention already in the repo (`PortfolioDashboardContainer`,
 * `ClientLevelView`): `useSearchParams`, a validated read with a fallback, `{ replace: true }`
 * so navigating does not fill the history stack, and the default value DELETED rather than
 * written so a clean Radar keeps a clean URL.
 *
 * TASK-833 REMOVED A FOURTH PARAM, `radarSubject`. It addressed the in-place investigation
 * frame, and under DEC-1 the case is a canvas tile — addressed by `?canvas=` like every other
 * tile (TASK-836), which is one addressing mechanism for the working surface instead of a
 * private one for this panel. The Radar keeps the three params that are about the RADAR:
 * which tab, which row is expanded, whether the DNA rollup is open. Those are exactly what
 * "the signal is still where I left it" means once the case opens elsewhere.
 */
const PARAM_TAB = 'radarTab';
const PARAM_SIGNAL = 'radarSignal';
const PARAM_DNA = 'radarDna';

// TASK-833: `CLIENT_LEVEL_TABS` and `buildInvestigationContext` moved to
// `signalSubject.ts` / `SignalDossier.tsx` with the panel that used them. The deep-link
// allow-list is a property of the surface that WRITES the link, and that is now the dossier.

/**
 * TASK-732: does the server policy allow this disposition? No policy → no restriction.
 * TASK-923: …AND may THIS user take it. `allowed_roles` shipped in the contract from the
 * start and this function ignored it, so `escalate`/`resolve` rendered to every user on a
 * compliance row and the server refused the write.
 */
const policyAllows = (e: RadarEvent, action: string, role?: string | null): boolean =>
  policyPermits(e.action_policy, action, role);

// Alert severity → task priority for the make-task edge.
const ALERT_PRIORITY: Record<string, 'low' | 'medium' | 'high' | 'critical'> = {
  critical: 'critical',
  high: 'high',
  medium: 'medium',
  info: 'low',
};
const SEVERITY_RANK: Record<string, number> = { critical: 0, high: 1, medium: 2, info: 3 };

const chf = (v: number | null): string =>
  v == null ? '—' : `CHF ${Intl.NumberFormat('en-CH', { maximumFractionDigits: 0 }).format(v)}`;

// TASK-833: `timeAgo` and `rowAge` moved to `SignalUrgency.tsx` with the badges that use
// them, so the Radar row and the dossier measure age the same way by construction.

/**
 * TASK-824: the ISIN behind an instrument signal.
 *
 * `entity_key` is `isin:CH000…` / `sac:…` / `client:…` (`radar_service.resolve_entity`), so
 * the prefix is what says whether this signal is about an instrument at all. Anything else
 * yields `undefined` rather than a guess — focusing a swap panel on a sub-asset class would
 * be worse than not focusing it.
 */
const isinOf = (entityKey: string | null | undefined): string | undefined =>
  entityKey && entityKey.startsWith('isin:') ? entityKey.slice('isin:'.length) : undefined;

/** TASK-833: the same rule for a sub-asset-class subject — what `DriftBars` opens on. */
const sacOf = (entityKey: string | null | undefined): string | undefined =>
  entityKey && entityKey.startsWith('sac:') ? entityKey.slice('sac:'.length) : undefined;

/**
 * TASK-824: one line saying what this signal is, for a composer to write ABOUT.
 *
 * Built from what the row already renders — nothing is fetched and nothing is invented. It
 * becomes the draft's `topic`; the route appends "Focus of this email: {topic}" to its
 * DNA-grounded summary and refuses a follow-up that has neither facts nor a topic, so an
 * empty summary produces an honest refusal rather than a generic letter.
 */
const signalSummaryOf = (
  e: { action: string | null; entity_label?: string | null; entity_key?: string | null },
  c: { dna_note?: string | null; suggested_action?: string | null },
): string | undefined => {
  const subject = e.entity_label || e.entity_key || '';
  const action = e.action || '';
  const head = subject && action ? `${action}: ${subject}` : action || subject;
  const detail = c.dna_note || c.suggested_action || '';
  const line = detail ? `${head}. ${detail}` : head;
  return line.trim() || undefined;
};


const SOURCE_STYLE: Record<string, string> = {
  news: 'bg-purple-100 text-purple-800',
  cio: 'bg-blue-100 text-blue-800',
  drift: 'bg-amber-100 text-amber-800',
  dna: 'bg-emerald-100 text-emerald-800',
  kyc: 'bg-rose-100 text-rose-800', // TASK-618: meeting→KYC proposals pending review
  // TASK-731: the admitted compliance conditions (styling only — admission and actions
  // are server-owned; an unknown source falls back to gray below).
  screening: 'bg-red-100 text-red-800',
  kyc_expiry: 'bg-red-100 text-red-800',
  reprofile: 'bg-orange-100 text-orange-800',
  review: 'bg-rose-100 text-rose-800',
};

// onHide (TASK-600 review): the dashboard offers hiding the rail — it occupies
// canvas space; the preference is DashboardPage's to own/persist.
/**
 * TASK-924: `deskLens` is a PROP, not a hook call. The lens spans this rail and the Do column,
 * and two components each holding their own copy would let the chip row and the task list
 * disagree about which desk you are at. `DashboardPage` owns it (as it owns the rail toggles).
 */
const ChangeRadar: React.FC<{ onHide?: () => void; deskLens?: DeskLens }> = ({ onHide, deskLens }) => {
  const { t } = useTranslation('dashboard');
  const [searchParams, setSearchParams] = useSearchParams();

  const rawTab = searchParams.get(PARAM_TAB) as Tab | null;
  const tab: Tab = rawTab && TABS.includes(rawTab) ? rawTab : DEFAULT_TAB;
  const expanded = searchParams.get(PARAM_SIGNAL);
  const dnaWallOpen = searchParams.get(PARAM_DNA) === '1';

  const patchParams = useCallback(
    (patch: Record<string, string | null>) => {
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(patch)) {
          // A default value is DELETED, never written — otherwise every dashboard visit
          // grows a `?radarTab=all` nobody asked for, and "is the Radar in its default
          // state?" stops being answerable from the URL.
          if (value === null || value === '' || (key === PARAM_TAB && value === DEFAULT_TAB)) {
            next.delete(key);
          } else {
            next.set(key, value);
          }
        }
        return next;
      }, { replace: true });
    },
    [setSearchParams],
  );

  // TASK-833: changing tab no longer abandons anything. It used to close the investigation,
  // because a frame mounted over a list it no longer belongs to is a lie about context — but
  // a tile on the canvas is not mounted over this list and is not a claim about it. An open
  // case surviving a tab change is the point of the case being a tile.
  const setTab = useCallback(
    (next: Tab) => patchParams({ [PARAM_TAB]: next }),
    [patchParams],
  );
  const toggleExpanded = useCallback(
    (id: string) => patchParams({ [PARAM_SIGNAL]: expanded === id ? null : id }),
    [patchParams, expanded],
  );

  // Live refresh is driven by the alert WebSocket: a `radar_updated` message invalidates the
  // ['radar'] query (useAlertWebSocket), which refetches this hook. No manual refetch proxy.
  // TASK-818 raised `RADAR_PAGE_LIMIT` to 100 and named it "so it stops being a magic number
  // in a call site" — and left this call site at 20, so the cap it raised never reached the
  // surface and the truncation notice it added went on reporting the old slice. Corrected
  // here because TASK-833's dossier resolves its subject out of THIS query: a second limit
  // would be a second query key, a second fetch, and a case that says "not in the current
  // Radar view" about a signal the Radar is showing.
  const { data, isLoading } = useRadar();

  // TASK-575: Alerts are a 4th radar tab (the ActionCenter inbox, re-homed). The alert WS is
  // already mounted globally in SecondaryNav; pull the initial list on mount.
  const alerts = useAlertStore((s) => s.alerts);
  const dismissAlert = useAlertStore((s) => s.dismiss);
  const snoozeAlert = useAlertStore((s) => s.snooze);
  const fetchAlerts = useAlertStore((s) => s.fetchAlerts);
  const fetchCounts = useAlertStore((s) => s.fetchCounts);
  const counts = useAlertStore((s) => s.counts);
  const makeTask = useMakeTaskFromSignal();
  const addSpecs = useCanvasStore((s) => s.addSpecs);
  useEffect(() => {
    // Deep review #5: this took the API's 50 default while the bell and the Alerts page fetched
    // 200 into the SAME store, so the TASK-653 primary count differed between surfaces.
    fetchAlerts({ limit: ALERT_PAGE_SIZE });
    fetchCounts();
  }, [fetchAlerts, fetchCounts]);

  // TASK-783: everything this surface RENDERS or COUNTS starts here. The store holds every
  // status — `fetchAlerts` sends none and `AlertService.list_alerts` only filters when given
  // one — and this component read that raw array for both the list and the red pulse while
  // calling the correct predicate on the line between them. So an alert dismissed on /alerts
  // stayed in the dashboard rail, and the pulse fired for alerts the number beside it had
  // already excluded. `unresolvedAlerts` is TASK-653's shared definition (active | read, plus
  // expired snoozes); this surface does not get its own.
  const openAlerts = useMemo(() => unresolvedAlerts(alerts), [alerts]);
  const criticalAlertCount = openAlerts.filter(
    (a) => a.level === 'critical' || a.level === 'high',
  ).length;
  // TASK-653: the tab pill shows THE primary triage number — unresolved alert
  // groups — matching the bell, the Alerts page, and Compliance.
  // Deliberately still fed the RAW array: `primaryTriageCount` prefers the server's count over
  // the fetched page and filters internally, so pre-filtering here would change nothing except
  // to hide that it does.
  const unresolvedCount = primaryTriageCount(alerts, counts);
  // TASK-924: an alert states its own `category` — it needs no `source_event` lookup, and
  // the two must agree, which is why both read the catalogue's vocabulary. An alert in a
  // category outside the chip row (`task`, `meeting`, …) is genuinely not compliance work and
  // is filtered out by a narrowed lens, not "unclassifiable".
  const lensedAlerts = useMemo(
    () =>
      !deskLens || deskLens.lens === 'all'
        ? openAlerts
        : openAlerts.filter((a) => a.category === deskLens.lens),
    [openAlerts, deskLens],
  );
  const sortedAlerts = useMemo(
    () => [...lensedAlerts].sort((a, b) => (SEVERITY_RANK[a.level] ?? 9) - (SEVERITY_RANK[b.level] ?? 9)),
    [lensedAlerts],
  );
  // TASK-603: identical causes aggregate — the radar consumes the same grouping
  // as the Alerts page; singleton causes keep the compact per-alert row.
  const { alertGroups, singleRadarAlerts } = useMemo(() => {
    const groups = groupAlerts(sortedAlerts);
    return {
      alertGroups: groups.filter((g) => g.alerts.length > 1),
      singleRadarAlerts: groups.filter((g) => g.alerts.length === 1).flatMap((g) => g.alerts),
    };
  }, [sortedAlerts]);
  const dismissAlertGroup = (memberIds: string[]) => memberIds.forEach((id) => dismissAlert(id));

  const makeTaskFromAlert = (alert: DashboardAlert) =>
    makeTask({
      title: alert.title,
      source: 'alert',
      description: alert.description,
      clientId: alert.relatedClientId,
      priority: ALERT_PRIORITY[alert.level] ?? 'medium',
      relatedEntityType: 'alert',
      relatedEntityId: alert.id,
    });

  const events = data?.events ?? [];
  const unresolved = data?.unresolved ?? [];

  // TASK-924: two independent cuts. The TAB is which kind of signal (radar rows vs news vs
  // alerts); the LENS is which desk's work it is. A CO on `all` still sees only compliance.
  const lensMatches = deskLens?.matches;
  const filtered = useMemo(
    () =>
      events.filter(
        (e) =>
          (tab === 'all' ? true : e.source === 'news') &&
          (lensMatches ? lensMatches(e.source_event) : true),
      ),
    [events, tab, lensMatches]
  );

  /**
   * TASK-924: the number beside a chip is exactly how many rows clicking it yields IN THE
   * CURRENT TAB. Counted off `data`/`alerts` already in hand — the ticket's "no second fetch",
   * and the only definition that cannot drift from what the list then renders.
   *
   * Deliberately NOT the alerts tab pill (`unresolvedCount`): that one is TASK-653's primary
   * triage number and has to match the bell, the Alerts page and Compliance. A lens is a view,
   * not a redefinition of how much is outstanding.
   */
  const lensCounts = useMemo(() => {
    const tally = Object.fromEntries(LENSES.map((l) => [l, 0])) as Record<Lens, number>;
    const NARROW = LENSES.filter((l) => l !== 'all');
    if (tab === 'alerts') {
      // An alert states its own category. One outside the chip row (`task`, `meeting`) is
      // genuinely not this desk's work and is counted only in `all` — the same thing the
      // filter does with it.
      openAlerts.forEach((a) => {
        tally.all += 1;
        if (a.category in tally) tally[a.category as Lens] += 1;
      });
    } else {
      events
        .filter((e) => (tab === 'all' ? true : e.source === 'news'))
        .forEach((e) => {
          tally.all += 1;
          const category = deskLens?.categoryOf(e.source_event);
          if (category === undefined) {
            // Unclassifiable → `matches` lets it through EVERY lens, so it has to be counted
            // under every chip. Counting it only in `all` is how a backend without the
            // catalogue map ends up showing `0` on a chip that then renders the whole list.
            NARROW.forEach((l) => { tally[l] += 1; });
          } else if (category in tally) {
            tally[category as Lens] += 1;
          }
        });
    }
    return tally;
  }, [tab, events, openAlerts, deskLens]);

  // TASK-603 follow-up: the DNA-conflict EVENTS were still a per-ISIN wall on the
  // All/Internal tabs (grouping had only landed on the Alerts tab). Collapse them
  // behind one summary row when there are more than three.
  // TASK-726: the rollup's open/closed state is part of "the signal is still visible after
  // returning" — a DNA subject investigated from inside the wall must not come back to a
  // collapsed wall, so it rides in the URL with the tab and the focused signal.
  const { dnaEvents, otherEvents } = useMemo(() => {
    const dna = filtered.filter((e) => e.source === 'dna');
    return dna.length > 3
      ? { dnaEvents: dna, otherEvents: filtered.filter((e) => e.source !== 'dna') }
      : { dnaEvents: [], otherEvents: filtered };
  }, [filtered]);
  // TASK-725: the DNA summary comes from the SERVER's book-wide totals, not from the
  // rendered array. `dnaEvents` is `useRadar()` capped and then tab-filtered, so counting
  // it produced a number that shrank as the page shrank — and it was labelled "positions"
  // when it was radar entity groups. `affected_positions` is the real distinct-position
  // count, and a position touched by both CIO and DNA is counted once.
  const dnaSummary = data?.summary?.by_source?.dna;

  // TASK-818: what this page is NOT showing. TASK-725 stopped the BANNER counting a
  // truncated array and stopped one line above the list that still was one — the honest
  // book-wide total sat directly over a silent top-N slice, so the surface contradicted
  // itself in a single glance.
  //
  // Measured against `events`, not `filtered`: the page cut and the tab filter are two
  // different cuts, and blaming the page for rows the advisor themselves filtered out would
  // overstate it. `total` is the server's count of every resolved row (radar_service.py:824),
  // computed before the slice.
  const pageTotal = data?.total ?? 0;
  const notListed = Math.max(0, pageTotal - events.length);
  // TASK-818: the rollup's own shortfall. `radar_entity_groups` is the server's count of
  // DNA groups over the whole book; `dnaEvents` is what survived the page limit. The banner
  // and the list beneath it must never disagree without saying so.
  const dnaNotListed = dnaSummary
    ? Math.max(0, dnaSummary.radar_entity_groups - dnaEvents.length)
    : 0;
  const maxImpact = useMemo(
    () => Math.max(1, ...filtered.map((e) => e.impact_score ?? 0)),
    [filtered]
  );

  /**
   * TASK-833: Investigate SUMMONS THE CASE onto the canvas — it no longer replaces this list.
   *
   * The summon carries the same signal identity Swap and Email already carry (TASK-726/729),
   * and `tileIdentity` keys on `subjectKey`, so two signals for one client are two tiles
   * rather than one silently updated in place.
   *
   * Only the identity keys travel. Everything the dossier renders — the policy, the alert
   * ids, the exposure — it resolves for itself out of the Radar payload, because a followed
   * link restores a tile from `URL_SAFE_PROPS` alone (TASK-836) and props that survive a
   * click but not a link are the worst kind.
   */
  const openInvestigation = (e: RadarEvent, c: ImpactedClient) => {
    addSpecs([{
      component: 'SignalDossier',
      props: {
        clientId: c.client_id,
        subjectKey: c.subject_key ?? undefined,
        sourceEvent: e.source_event ?? undefined,
        entityKey: e.entity_key ?? undefined,
        focusIsin: isinOf(e.entity_key),
        focusSac: sacOf(e.entity_key),
        originType: 'change_radar',
        originId: e.id,
      },
    }]);
    // Leave the row EXPANDED. The advisor is now working in two places, and coming back to a
    // collapsed list they have to find their way into again is the thing TASK-726 fixed.
    patchParams({ [PARAM_SIGNAL]: e.id });
  };

  // TASK-783: `openAlerts`, not `alerts`. A book whose every alert has been dismissed has
  // nothing outstanding, which is what this guard means by "nothing across the book" — reading
  // the raw array kept the rail on screen to say "You're all caught up."
  // TASK-924: the predicate is shared with `useDeskLens`, which uses it to decide whether the
  // lens has a control on screen at all. Two copies would drift.
  if (!isLoading && !signalsRailRenders(events, unresolved, openAlerts)) {
    return null; // nothing across the book → don't take up morning space
  }

  return (
    <div className="bg-white rounded-lg border border-gray-200 shadow-sm p-4 sm:p-5 mb-6">
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div className="flex min-w-0 items-center gap-2">
          <Radar className="w-5 h-5 shrink-0 text-[#002B45]" />
          <h4 className="truncate font-semibold text-gray-900">{t('radar.title', 'Change Radar — across your book')}</h4>
          {onHide && (
            <button
              type="button"
              onClick={onHide}
              title={t('radar.hide', 'Hide Change Radar')}
              aria-label={t('radar.hide', 'Hide Change Radar')}
              className="shrink-0 rounded-md p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700"
            >
              <PanelLeftClose className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="flex flex-wrap gap-1 text-xs">
          {(['all', 'news', 'alerts'] as Tab[]).map((key) => (
            <button
              key={key}
              onClick={() => setTab(key)}
              className={`relative px-3 py-1 rounded-full font-medium transition-colors ${
                tab === key ? 'bg-[#002B45] text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
              }`}
            >
              {t(`radar.tabs.${key}`, key)}
              {key === 'alerts' && unresolvedCount > 0 && (
                <span
                  title={t('radar.unresolvedGroups', {
                    count: unresolvedCount,
                    defaultValue: `${unresolvedCount} unresolved alert groups`,
                  })}
                  className={`ml-1 inline-flex h-4 min-w-[16px] items-center justify-center rounded-full px-1 text-[9px] font-semibold ${
                    criticalAlertCount > 0
                      ? 'animate-pulse bg-red-500 text-white'
                      : tab === 'alerts'
                        ? 'bg-white/25 text-white'
                        : 'bg-gray-300 text-gray-700'
                  }`}
                >
                  {unresolvedCount}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>

      {/* TASK-924: the desk lens. Today had no role check — it was identical for the RM and
          the CO, and the catalogue has tagged every admitted signal with a category all along.
          A chip row on the ONE attention surface, per the standing rule: no new route, no CO
          dashboard. `all` is always present and always one click away, because this is a lens
          and not a permission — what a desk may DO is `allowed_roles` on the server policy. */}
      {deskLens && (
        <div role="group" aria-label={t('radar.lens.label', 'Filter by desk')} className="mb-4 flex flex-wrap gap-1 text-xs">
          {LENSES.map((key) => (
            <button
              key={key}
              type="button"
              onClick={() => deskLens.setLens(key)}
              aria-pressed={deskLens.lens === key}
              className={`rounded-full px-2.5 py-1 font-medium transition-colors ${
                deskLens.lens === key
                  ? 'bg-[#002B45] text-white'
                  : 'bg-gray-50 text-gray-600 ring-1 ring-inset ring-gray-200 hover:bg-gray-100'
              }`}
            >
              {t(`radar.lens.${key}`, LENS_LABEL[key])}
              <span className={`ml-1 tabular-nums ${deskLens.lens === key ? 'text-white/70' : 'text-gray-400'}`}>
                {lensCounts[key]}
              </span>
            </button>
          ))}
        </div>
      )}

      {tab === 'alerts' ? (
        sortedAlerts.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-6">
            {/* TASK-924: "all caught up" is a claim about the BOOK, and only the unlensed list
                may make it. Narrowed to a desk with nothing on it while other desks are full,
                this said the opposite of the truth — the same rule the Do column follows. */}
            {openAlerts.length > sortedAlerts.length
              // `others`, not `count`: `count` puts i18next into plural lookup, and this key has
              // no `_one`/`_other` pair. It falls back to the base key today — but the pair is
              // what a translator would add next, and then the fallback silently stops.
              ? t('radar.noAlertsOnDesk', {
                  desk: LENS_LABEL[deskLens?.lens ?? 'all'].toLowerCase(),
                  others: openAlerts.length,
                  defaultValue:
                    `No ${LENS_LABEL[deskLens?.lens ?? 'all'].toLowerCase()} alerts — ` +
                    `${openAlerts.length} on other desks.`,
                })
              : t('radar.noAlerts', "You're all caught up.")}
          </p>
        ) : (
          <div className="space-y-2">
            {alertGroups.map((group) => (
              <AlertGroupCard key={group.key} group={group} onDismissGroup={dismissAlertGroup} />
            ))}
            {singleRadarAlerts.map((a) => (
              <AlertRow
                key={a.id}
                alert={a}
                onDismiss={dismissAlert}
                onSnooze={snoozeAlert}
                onMakeTask={makeTaskFromAlert}
              />
            ))}
          </div>
        )
      ) : isLoading && events.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6">{t('radar.loading', 'Scanning your book…')}</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-500 text-center py-6">{t('radar.empty', 'No changes in this view.')}</p>
      ) : (
        <div className="space-y-2">
          {/*
            TASK-822 AC4: the ordering, stated rather than inferred. Advisors currently infer
            it and infer wrong — `_rank_normalize_by_source` gives EACH source's top row 1.0
            and sorts on that first, so sources interleave by within-source rank rather than by
            age or exposure. That is deliberate (it stops one loud source drowning out a
            quieter one) and it is why CHF 142'268 at 28d can sit above CHF 126'758 at 28d.
            Unexplained, it reads as arbitrary.
          */}
          <p data-testid="radar-ordering-note" className="px-1 text-[11px] text-gray-400">
            {t('radar.ordering',
              'Ordered by: mandatory obligations first (breached, then undated, then by ' +
              'urgency class), then advisory signals ranked within each source so one source ' +
              'cannot crowd out another.')}
          </p>
          {notListed > 0 && (
            <p
              data-testid="radar-truncation-notice"
              className="rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800"
            >
              {/*
                TASK-756: the default value carries the numbers already substituted, the way
                every other count on this surface does — a locale that has not been filled in
                must render a real sentence, never a key or an uninterpolated placeholder.
              */}
              {t('radar.truncation', {
                shown: events.length,
                total: pageTotal,
                defaultValue:
                  `Showing the ${events.length} highest-ranked of ${pageTotal} signals — ` +
                  'mandatory obligations first, then advisory rows by impact. The rest are ' +
                  'counted in the totals above but not listed.',
              })}
            </p>
          )}
          {dnaEvents.length > 0 && (
            <div className="rounded-lg border border-orange-200 bg-orange-50/50">
              <button
                type="button"
                onClick={() => patchParams({ [PARAM_DNA]: dnaWallOpen ? null : '1' })}
                className="flex w-full items-center gap-2 px-3 py-2.5 text-left"
                aria-expanded={dnaWallOpen}
              >
                <AlertTriangle className="h-4 w-4 shrink-0 text-orange-500" aria-hidden />
                <span className="min-w-0 text-sm font-medium text-gray-900">
                  {t('radar.dna.title', 'DNA conflicts')}
                  {dnaSummary ? (
                    <span className="ml-1 font-normal text-gray-500">
                      {/*
                        TASK-756: counts and units go through the translation layer TOGETHER.
                        Interpolating a number into an English noun ("3 positions") and
                        translating the surrounding sentence separately is how the German
                        render ends up half-English — and how a plural rule that differs from
                        English silently produces the wrong word.
                      */}
                      · {t('radar.dna.positions', {
                        count: dnaSummary.affected_positions,
                        defaultValue: `${dnaSummary.affected_positions} positions`,
                      })} ·{' '}
                      {t('radar.dna.clients', {
                        count: dnaSummary.affected_clients,
                        defaultValue: `${dnaSummary.affected_clients} clients`,
                      })}
                      {dnaSummary.exposure_chf > 0 &&
                        ` · ${t('radar.dna.exposure', {
                          amount: Math.round(dnaSummary.exposure_chf).toLocaleString('de-CH').replace(/,/g, "'"),
                          defaultValue: `CHF ${Math.round(dnaSummary.exposure_chf).toLocaleString('de-CH').replace(/,/g, "'")}`,
                        })}`}
                      <span className="ml-1 text-gray-400">
                        · {t('radar.dna.scope', 'across the whole book')}
                      </span>
                    </span>
                  ) : (
                    // No server summary → say so rather than count the visible page and
                    // present a truncated number as a total (TASK-722/725).
                    <span className="ml-1 font-normal text-gray-400">
                      · {t('radar.dna.totalsUnavailable', 'totals unavailable')}
                    </span>
                  )}
                </span>
                <span className="ml-auto shrink-0 text-xs text-blue-600">
                  {dnaWallOpen
                    ? t('radar.dna.collapse', 'collapse')
                    : t('radar.dna.expand', 'expand')}
                </span>
              </button>
              {dnaWallOpen && (
                <div className="space-y-2 px-2 pb-2">
                  {/*
                    TASK-818: the rollup says what it is not listing. The banner above states
                    the book-wide total; expanding it used to reveal a shorter list with no
                    comment — live, "31 positions · CHF 4'683'029 · across the whole book"
                    over 11 instruments totalling CHF 3'213'838, with a third of the stated
                    exposure invisible. A banner and its own list must never disagree in
                    silence.
                  */}
                  {dnaNotListed > 0 && (
                    <p
                      data-testid="radar-dna-truncation-notice"
                      className="rounded border border-orange-200 bg-white/70 px-2 py-1.5 text-xs text-orange-800"
                    >
                      {t('radar.dna.truncation', {
                        listed: dnaEvents.length,
                        total: dnaSummary?.radar_entity_groups ?? 0,
                        defaultValue:
                          `Listing ${dnaEvents.length} of ` +
                          `${dnaSummary?.radar_entity_groups ?? 0} conflicting instruments — ` +
                          'the rest are counted in the total above but did not fit this page.',
                      })}
                    </p>
                  )}
                  {dnaEvents.map((e) => (
                    <RadarRow
                      key={e.id}
                      event={e}
                      maxImpact={maxImpact}
                      open={expanded === e.id}
                      onToggle={() => toggleExpanded(e.id)}
                      onMakeTask={makeTask}
                      onInvestigate={(c) => openInvestigation(e, c)}
                    />
                  ))}
                </div>
              )}
            </div>
          )}
          {otherEvents.map((e) => (
            <RadarRow
              key={e.id}
              event={e}
              maxImpact={maxImpact}
              open={expanded === e.id}
              onToggle={() => toggleExpanded(e.id)}
              onMakeTask={makeTask}
              onInvestigate={(c) => openInvestigation(e, c)}
            />
          ))}
        </div>
      )}

      {tab !== 'alerts' && unresolved.length > 0 && (
        <div className="mt-4 pt-3 border-t border-gray-100">
          {unresolved.map((u) => (
            <div key={u.id} className="flex items-start gap-2 text-xs text-amber-700 py-1">
              <AlertTriangle className="w-3.5 h-3.5 mt-0.5 flex-shrink-0" />
              <span>
                <b>{u.action || u.entity_label}</b> — {unresolvedCopy(t, u.unresolved_code)}
                {/*
                  TASK-722: the raw diagnostic ("Entity isin:X matched 3 client(s) but
                  resolved to zero CHF exposure") is an engineer's string and was being shown
                  to relationship managers. The ROW stays — dropping an unresolved signal
                  silently is worse than reporting it — but the wording is now legible, and
                  the diagnostic is kept for whoever can actually act on it.
                  Vite strips this branch entirely from production builds.
                */}
                {import.meta.env.DEV && u.unresolved_reason && (
                  <span className="block mt-0.5 text-[10px] text-gray-400 font-mono">
                    {u.unresolved_reason}
                  </span>
                )}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

/**
 * TASK-722: RM-facing wording for an unresolved radar row, keyed on the backend's stable
 * `unresolved_code`. Deliberately says what it means for the RM ("we could not price this")
 * rather than what the pipeline did, and never implies the signal is unimportant — it is
 * unresolved, not absent.
 */
const UNRESOLVED_COPY: Record<string, string> = {
  zero_exposure:
    'signal received, but no holding could be priced for the affected client(s) — exposure unknown',
  no_entity: 'signal received without an identifiable instrument or sector — cannot be placed',
  // TASK-843: it names somebody, and that somebody is not in the book. Says "no longer in
  // your book" rather than "unknown client", because the alert usually still carries a real
  // NAME — it was rendering "Confirm document type: passport / Matteo Bernardis" as live work
  // against a client id with no row behind it. Telling the advisor the name is unknown would
  // contradict the name they can see; what is actually missing is the client.
  dangling_client:
    'signal received for a client that is no longer in your book — nothing here can be acted '
    + 'on, and the underlying record needs cleaning up',
};

/**
 * TASK-756: keyed on the backend's stable `unresolved_code`, which is exactly what makes the
 * diagnostics separately translatable. The English strings above stay as the DEFAULT VALUE
 * for each key, so a locale that has not been filled in yet renders the honest English
 * sentence rather than the key — a raw `radar.unresolved.zero_exposure` on the dashboard
 * would be a worse lie than English, because it says nothing at all.
 */
export const unresolvedCopy = (
  t: (key: string, fallback: string) => string,
  code?: string | null,
): string =>
  code && UNRESOLVED_COPY[code]
    ? t(`radar.unresolved.${code}`, UNRESOLVED_COPY[code])
    : t('radar.unresolved.fallback', 'could not be resolved to a portfolio position');

const RadarRow: React.FC<{
  event: RadarEvent;
  maxImpact: number;
  open: boolean;
  onToggle: () => void;
  onMakeTask: (input: SignalTaskInput) => void;
  /** TASK-726: open THIS subject in the investigation frame. */
  onInvestigate?: (client: ImpactedClient) => void;
}> = ({ event: e, maxImpact, open, onToggle, onMakeTask, onInvestigate }) => {
  const { t } = useTranslation('dashboard');
  // TASK-841 / TASK-820: the honest fallback, translated once for the three places this
  // row can render a client. `alerts.scope.client` is the SAME key `groupAlerts` uses, so
  // the Radar and the Alerts page cannot drift into two different ways of saying it.
  const unavailable = t('alerts.scope.client', 'Client (name unavailable)');
  // TASK-923: the row's actions are gated on the viewer's role, not only on the policy's
  // disposition list — a compliance row must not offer an advisor a button that will 403.
  const role = useViewerRole();
  // TASK-498: instant no-LLM summon — push a widget spec straight into the canvas sink.
  const addSpecs = useCanvasStore((s) => s.addSpecs);
  const navigate = useNavigate();
  const barPct = Math.round(((e.impact_score ?? 0) / maxImpact) * 100);
  // TASK-731/732: everything below is read off the SERVER payload — the row holds no
  // source-event rule table of its own.
  const mandatory = e.mandatory ?? false;
  const exposureNA = e.exposure_not_applicable ?? false;
  const canAssign = policyAllows(e, 'assign', role);
  return (
    <div className="rounded-lg border border-gray-100 hover:bg-gray-50 transition-colors">
      {/*
        TASK-844: the row is a STACK, not a single flex line.

        It was `flex items-center gap-3` with four siblings: chevron, a `w-16` badge column,
        the content, and the source tag. Measured in the live rail at a 1600px viewport, that
        divided 261px as 16 / 64 / **57** / 64 — the two fixed badge columns took 128px
        between them and the title, the client, the CHF figure and both urgency badges shared
        57px. "Re-profile due: Johann Mueller" rendered as "Re-profil…" over two lines, the
        metadata line wrapped into a 68px-tall vertical stack, and every row stood 132px tall
        while saying almost nothing. The `w-16` column could not even hold its own badge:
        the Mandatory pill measures 68px inside a 64px box.

        The rail is a fixed `xl:w-[280px] 2xl:w-[320px]`, so no viewport rescues this — the
        row has to fit the rail rather than the rail grow for the row. Stacking gives the
        title the full 237px of content width and lets the badges wrap on their own line
        instead of competing with it.
      */}
      <button onClick={onToggle} className="w-full flex items-start gap-2 p-3 text-left">
        <span className="pt-0.5">
          {open ? (
            <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0" />
          ) : (
            <ChevronRight className="w-4 h-4 text-gray-400 flex-shrink-0" />
          )}
        </span>
        <div className="min-w-0 flex-1 space-y-1.5">
          {/*
            Line 1 — WHAT this is, at full width, with the source tag beside it. The tag was
            the last sibling of the old flex row, so it took a fixed 64px off the title on
            every row; here it shrinks to its label and the title takes the rest.
          */}
          <div className="flex items-start gap-2">
            {/* TASK-603/607: two lines before ellipsis — don't truncate our own payload */}
            <p className="min-w-0 flex-1 text-sm font-medium text-gray-900 line-clamp-2">
              {e.action}: {e.entity_label || e.entity_key}
            </p>
            {e.source && (
              <span className={`shrink-0 px-2 py-0.5 rounded-full text-[10px] font-medium ${SOURCE_STYLE[e.source] || 'bg-gray-100 text-gray-700'}`}>
                {e.source}
              </span>
            )}
          </div>
          {/*
            Line 2 — HOW URGENT, on its own row so the two axes (TASK-821) and the breach age
            (TASK-822) can sit side by side instead of wrapping one badge per line. `w-16` on
            the impact bar keeps the advisory bar the width it always was; the mandatory badge
            is no longer boxed into 64px it overflowed.
          */}
          <div className="flex flex-wrap items-center gap-1.5">
            {mandatory ? (
            // TASK-731: a mandatory condition has no impact score — the band IS its rank.
            // An empty bar here would read as "lowest impact", the exact opposite.
            // TASK-821: the OBLIGATION band. `mandatory` is a policy flag from the TASK-732
            // catalogue; `level` on the Alerts page is an emitter severity. They are
            // orthogonal and disagree by design — `reprofile_due` is `level="medium"` and
            // `mandatory=True` at the same time, a low-severity signal the firm has no
            // discretion to ignore. Rendered in the same 16px slot as the advisory impact
            // bar, this badge read as the top of one urgency scale, so the advisor saw a
            // contradiction where there were two facts.
            <span
              data-testid="radar-mandatory-badge"
              title={t('radar.mandatoryHint',
                'An obligation the firm has no discretion to ignore. Separate from severity — a mandatory signal can be low severity.')}
              className="inline-flex items-center rounded-full bg-red-100 px-1.5 py-0.5 text-[9px] font-semibold uppercase text-red-800"
            >
              {t('radar.mandatory', 'Mandatory')}
            </span>
          ) : (
            <div className="h-1.5 w-16 shrink-0 bg-gray-100 rounded-full overflow-hidden">
              <div className="h-full bg-[#002B45] rounded-full" style={{ width: `${barPct}%` }} />
            </div>
          )}
            {/*
              TASK-833: the two urgency axes (TASK-821) and the breach age (TASK-822) render
              from `SignalUrgency`, which the dossier mounts too. They were inline here, and
              a second copy of "which age is this, and what is it the age of" is precisely
              the defect shape this epic keeps finding.
            */}
            <SignalUrgency facts={e} />
          </div>
          {/*
            Line 3 — WHO and HOW MUCH. `flex-wrap` with a tight row gap: at 280px the client
            name and the CHF figure fit on one line for most rows and wrap cleanly when they
            do not, instead of each metadatum taking a line of its own.
          */}
          <p className="text-xs text-gray-500 flex flex-wrap items-center gap-x-2 gap-y-0.5">
            {/*
              TASK-823: the client's NAME on the collapsed row, not just a count.
              `impacted_clients[].client_name` has always been in the payload
              (`radar_service.py:496`) and the row rendered `client_count` alone — so four
              mandatory rows read "1" apiece and the advisor had to expand each to find out
              who it was about. Beyond one client the count is the honest summary; the names
              belong in the expansion.
            */}
            <span className="inline-flex items-center gap-1" data-testid="radar-row-clients">
              <Users className="w-3 h-3" />
              {/* TASK-841: never the raw value. It was `?? client_id` on the server, so
                  this rendered an opaque UUID for one live row. */}
              {e.client_count === 1 && namesAClient(
                e.impacted_clients[0]?.client_name, e.impacted_clients[0]?.client_id)
                ? clientDisplayName(
                    e.impacted_clients[0].client_name,
                    e.impacted_clients[0].client_id,
                    unavailable)
                : e.client_count}
            </span>
            {/* TASK-731: "not applicable" is a different fact from "unknown" — never a
                dash that reads as a missing valuation, and never a fabricated figure. */}
            <span data-testid="radar-exposure">
              {exposureNA
                ? t('radar.exposureNotApplicable', 'CHF exposure not applicable')
                : chf(e.total_exposure_chf)}
            </span>
          </p>
        </div>
      </button>

      {open && (
        <div className="px-4 pb-3 pl-11 space-y-1">
          {e.impacted_clients.map((c) => (
            <div key={c.client_id} className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1 text-xs text-gray-700 py-1 border-t border-gray-50">
              <span className="min-w-0 flex-1 truncate font-medium">
                {clientDisplayName(c.client_name, c.client_id, unavailable)}
              </span>
              <span className="flex flex-wrap items-center gap-2">
                {!exposureNA && <span>{chf(c.exposure_chf)}</span>}
                {c.exposure_pct != null && <span className="text-gray-400">{c.exposure_pct.toFixed(1)}%</span>}
                {c.drift_caused != null && <span className="text-red-600">{c.drift_caused > 0 ? '+' : ''}{c.drift_caused}pp</span>}
                {c.swap_candidate?.candidate_isin && (
                  <span className="text-emerald-700">↪ {c.swap_candidate.candidate_isin}</span>
                )}
                {/*
                  TASK-726: the subject's route INTO portfolio context. Rendered only when
                  the subject has a durable key — without one there is nothing stable to open
                  on, and a frame keyed on a `change_events` id would dangle on the next tick.
                */}
                {onInvestigate && c.subject_key && (
                  <button
                    type="button"
                    data-testid="radar-investigate"
                    data-subject-key={c.subject_key}
                    onClick={() => onInvestigate(c)}
                    className="flex items-center gap-0.5 px-2 py-0.5 rounded-full font-medium bg-[#002B45] text-white hover:bg-[#003d63] transition-colors"
                  >
                    <Search className="w-3 h-3" /> {t('radar.actions.investigate', 'Investigate')}
                  </button>
                )}
                {/* TASK-618: meeting→KYC proposals deep-link into the workspace rail */}
                {e.source === 'kyc' && (
                  <button
                    type="button"
                    data-testid="radar-kyc-review"
                    onClick={() => navigate(`/clients/${c.client_id}/kyc-workspace`)}
                    className="px-2 py-0.5 rounded-full font-medium bg-rose-600 text-white hover:bg-rose-700 transition-colors"
                  >
                    {t('radar.actions.review', 'Review')}
                  </button>
                )}
                {/*
                  TASK-731: Swap/Email are PORTFOLIO affordances. A compliance condition
                  carries no market exposure (`exposure_not_applicable` on the payload),
                  so they are absent — the row's actions come from the server contract.
                */}
                {/*
                  TASK-824: Swap is offered only where the server says a candidate EXISTS.
                  It was gated on `!exposureNA` alone, so it was offered on rows the engine
                  had already answered: live, all 11 conflicts returned "No compliant swap",
                  the panel opened on fit 42 → 42 with 0 resolved, and every sub-asset weight
                  identical. The row renders `swap_candidate` one line above (`:858`) — the
                  answer was on screen while the button promised otherwise. The panel's
                  explanation of WHY no swap exists is good and belongs in TASK-830; it is
                  not a primary action.
                */}
                {!exposureNA && c.swap_candidate?.candidate_isin && (
                <button
                  type="button"
                  onClick={() =>
                    addSpecs([{
                      component: 'BeforeAfter',
                      // TASK-726/729: the summon carries the signal's ORIGIN IDENTITY, not
                      // `{ clientId }` alone. Without `subjectKey` the CIO and DNA views for
                      // one client collapsed onto a single tile (`tileIdentity`), so opening
                      // the second silently replaced the first and the RM believed they were
                      // looking at the signal they had just clicked.
                      props: {
                        clientId: c.client_id,
                        subjectKey: c.subject_key ?? undefined,
                        sourceEvent: e.source_event ?? undefined,
                        entityKey: e.entity_key ?? undefined,
                        // TASK-824: the instrument the advisor actually clicked. Without it
                        // the panel opened on whichever conflict came first.
                        focusIsin: isinOf(e.entity_key),
                        alertIds: c.alert_ids ?? undefined,
                        signalGroupKey: e.group_key ?? undefined,
                        originType: 'change_radar',
                        originId: e.id,
                      },
                    }])
                  }
                  className="px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  {t('radar.actions.swap', 'Swap')}
                </button>
                )}
                {!exposureNA && (
                <button
                  type="button"
                  onClick={() =>
                    addSpecs([{
                      component: 'EmailDraft',
                      props: {
                        clientId: c.client_id,
                        subjectKey: c.subject_key ?? undefined,
                        sourceEvent: e.source_event ?? undefined,
                        entityKey: e.entity_key ?? undefined,
                        focusIsin: isinOf(e.entity_key),
                        alertIds: c.alert_ids ?? undefined,
                        signalGroupKey: e.group_key ?? undefined,
                        // TASK-824: what the email is ABOUT, built from what this row already
                        // renders. It becomes the composer's `topic`, which is the difference
                        // between a draft about this conflict and the client's last letter.
                        signalSummary: signalSummaryOf(e, c),
                        originType: 'change_radar',
                        originId: e.id,
                      },
                    }])
                  }
                  className="px-2 py-0.5 rounded-full font-medium bg-gray-100 text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  {t('radar.actions.email', 'Email')}
                </button>
                )}
                {/* TASK-732: task creation is the `assign` disposition — server-gated. */}
                {canAssign && (
                <button
                  type="button"
                  title={t('radar.actions.createTaskTitle', 'Create a task for this client')}
                  onClick={() =>
                    onMakeTask({
                      // TASK-841: a task's TITLE is durable — an id baked into one outlives
                      // every fix to the surface that produced it.
                      title: `${e.action}: ${e.entity_label || e.entity_key} — `
                        + clientDisplayName(c.client_name, c.client_id, unavailable),
                      source: e.source || 'radar',
                      clientId: c.client_id,
                      // TASK-724: bind to the DURABLE subject. `e.id` is a `change_events`
                      // row, deleted and recreated on the next 15-minute tick, so it was a
                      // back-reference that dangled almost immediately. The alert id alone
                      // would not do either — CIO and DNA share `isin:{isin}` and only one
                      // id survived aggregation (TASK-747), so the full set travels too.
                      subjectKey: c.subject_key ?? undefined,
                      subjectSourceEvent: e.source_event ?? undefined,
                      originGroupKey: e.group_key ?? undefined,
                      primaryAlertId: c.primary_alert_id ?? c.alert_id ?? undefined,
                      contributingAlertIds: c.alert_ids,
                      relatedEntityType: c.primary_alert_id || c.alert_id ? 'alert' : 'radar_event',
                      relatedEntityId: c.primary_alert_id ?? c.alert_id ?? e.id,
                    })
                  }
                  className="flex items-center gap-0.5 px-2 py-0.5 rounded-full font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
                >
                  <ListPlus className="w-3 h-3" /> {t('radar.actions.task', 'Task')}
                </button>
                )}
              </span>
            </div>
          ))}
          {e.suggested_batch_action && canAssign && (
            <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
              <p className="min-w-0 flex-1 text-xs text-gray-500 italic">{e.suggested_batch_action}</p>
              <button
                type="button"
                onClick={() =>
                  onMakeTask({
                    title: e.suggested_batch_action || `${e.action}: ${e.entity_label || e.entity_key}`,
                    source: e.source || 'radar',
                    // A batch action spans several subjects, so it binds to the GROUP, not to
                    // any one subject — deliberately no `subjectKey`. TASK-726 keeps batch
                    // disposition disabled until one action can update every included
                    // subject under a shared batch identity.
                    originGroupKey: e.group_key ?? undefined,
                    subjectSourceEvent: e.source_event ?? undefined,
                    relatedEntityType: 'radar_event',
                    relatedEntityId: e.id,
                  })
                }
                className="flex items-center gap-0.5 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-600 text-white hover:bg-indigo-700 transition-colors"
              >
                <ListPlus className="w-3 h-3" /> {t('radar.actions.makeTask', 'Make task')}
              </button>
            </div>
          )}
          {e.news_url && (
            <a href={e.news_url} target="_blank" rel="noreferrer" className="text-xs text-blue-600 hover:underline">
              {t('radar.actions.openArticle', 'Open article →')}
            </a>
          )}
        </div>
      )}
    </div>
  );
};

export default ChangeRadar;
